import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
} from '@mui/material';
import './platform.css';  // your custom styles (optional)
import {
  ACTIVE_CONTROLLER_SFX_COLLECTION_KEY,
  createControllerSoundSet,
  playControllerSoundFromSet,
  warmControllerSounds,
} from './controllerSfx';

const CONTROLLER_REPEAT_INITIAL_DELAY_MS = 240;
const CONTROLLER_REPEAT_INTERVAL_MS = 85;
const CONTROLLER_CONNECTED_STATE_KEY = 'controller:connected-active';
const GAMES_TO_PLATFORMS_TRANSITION_KEY = 'transition:games-to-platforms';

const getInitialPrimaryGamepad = () => {
  if (!navigator.getGamepads) return null;
  const pads = navigator.getGamepads();
  for (let i = 0; i < pads.length; i += 1) {
    if (pads[i] && pads[i].connected) {
      return pads[i];
    }
  }
  return null;
};

export default function Platforms() {
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState(() => window.__platformsCache || []);
  const [loadingPlatforms, setLoadingPlatforms] = useState(() => !(window.__platformsCache || []).length);
  const [platformsLoadError, setPlatformsLoadError] = useState('');
  const [platformsReloadToken, setPlatformsReloadToken] = useState(0);
  const [newGames, setNewGames] = useState([]);
  const [selectedNavIndex, setSelectedNavIndex] = useState(0);
  const [forceMobileLayout, setForceMobileLayout] = useState(false);
  const [gamepadConnected, setGamepadConnected] = useState(() => Boolean(getInitialPrimaryGamepad()));
  const [gamepadName, setGamepadName] = useState(() => getInitialPrimaryGamepad()?.id || '');
  const [controllerNeedsInteraction, setControllerNeedsInteraction] = useState(false);
  const gamepadRafRef = useRef(null);
  const gamepadRepeatStateRef = useRef({});
  const gamepadConnectionStateRef = useRef({ connected: false, name: '' });
  const controllerSoundsRef = useRef({ back: null, select: null, confirm: null });
  const [controllerToast, setControllerToast] = useState({ open: false, message: '' });
  const [showEntryWhiteOverlay, setShowEntryWhiteOverlay] = useState(false);
  const [entryWhiteOverlayFadeOut, setEntryWhiteOverlayFadeOut] = useState(false);
  const controllerToastTimerRef = useRef(null);
  const previousGamepadConnectedRef = useRef(null);
  const controllerSelectionChangePendingRef = useRef(false);
  const previousSelectedNavIndexRef = useRef(0);
  const lastDesktopPlatformIndexRef = useRef(0);
  const desktopSelectionInitializedRef = useRef(false);
  const coverflowStageRef = useRef(null);
  const isMobileRef = useRef(false);
  const controllerNeedsInteractionRef = useRef(false);
  const selectedNavIndexRef = useRef(0);
  const navItemsRef = useRef([]);
  const selectNavByHorizontalDeltaRef = useRef(null);
  const selectNavByVerticalDeltaRef = useRef(null);
  const navigateToPathRef = useRef(null);
  const pendingSelectedPathRef = useRef(null);
  const PLATFORM_SELECTION_STORAGE_KEY = 'platforms:lastPath';
  const PLATFORM_TO_GAMES_TRANSITION_KEY = 'transition:platform-to-games';

  const width = useWindowWidth();
  const isMobile = width < 600; // tweak this breakpoint as needed
  const useMobileLayout = isMobile || forceMobileLayout;

  const specialNavItems = useMemo(() => ([
    { key: 'all-games', logo: 'All_Games', alt: 'All Games', path: '/platforms/0' },
    { key: 'all-consoles', logo: 'All_Consoles', alt: 'All Consoles', path: '/consoles' },
    { key: 'soundtracks', logo: 'Soundtracks', alt: 'Soundtracks', path: '/platforms/100' },
    { key: 'steam', logo: 'Steam', alt: 'Steam Library', path: '/steam' },
    { key: 'favorites', logo: 'favorites', alt: 'Favorites', path: '/platforms/99' },
    { key: 'wanted', logo: 'Most Wanted', alt: 'Most Wanted', path: '/wanted' },
    { key: 'stats', logo: 'stats', alt: 'Stats', path: '/stats' },
  ]), []);
  const platformNavItems = useMemo(() => (
    platforms.map((p) => ({
      key: `platform-${p.id}`,
      logo: p.name,
      alt: p.name,
      gameCount: Number(p.game_count) || 0,
      label: `${p.name} (${Number(p.game_count) || 0})`,
      path: `/platforms/${p.id}`,
    }))
  ), [platforms]);
  const navItems = useMemo(() => (
    useMobileLayout ? [...specialNavItems, ...platformNavItems] : [...platformNavItems, ...specialNavItems]
  ), [useMobileLayout, specialNavItems, platformNavItems]);

  useEffect(() => {
    const activeCollectionKey =
      sessionStorage.getItem(ACTIVE_CONTROLLER_SFX_COLLECTION_KEY) || 'default';
    controllerSoundsRef.current = createControllerSoundSet(activeCollectionKey);
    warmControllerSounds(activeCollectionKey);
  }, []);

  const playControllerSound = useCallback((soundName) => {
    playControllerSoundFromSet(controllerSoundsRef.current, soundName);
  }, []);

  const showControllerToast = useCallback((message) => {
    if (controllerToastTimerRef.current) {
      clearTimeout(controllerToastTimerRef.current);
    }
    setControllerToast({ open: true, message });
    controllerToastTimerRef.current = setTimeout(() => {
      setControllerToast({ open: false, message: '' });
    }, 2200);
  }, []);

  useEffect(() => () => {
    if (controllerToastTimerRef.current) {
      clearTimeout(controllerToastTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const hasTransitionToken = Boolean(sessionStorage.getItem(GAMES_TO_PLATFORMS_TRANSITION_KEY));
    if (!hasTransitionToken) return;
    sessionStorage.removeItem(GAMES_TO_PLATFORMS_TRANSITION_KEY);
    setShowEntryWhiteOverlay(true);
    setEntryWhiteOverlayFadeOut(false);
  }, []);

  useEffect(() => {
    if (!showEntryWhiteOverlay) return;
    const fadeRaf = requestAnimationFrame(() => {
      setEntryWhiteOverlayFadeOut(true);
    });
    const hideTimer = setTimeout(() => {
      setShowEntryWhiteOverlay(false);
    }, 380);
    return () => {
      cancelAnimationFrame(fadeRaf);
      clearTimeout(hideTimer);
    };
  }, [showEntryWhiteOverlay]);

  const getPlatformGridStep = useCallback(() => {
    if (!useMobileLayout) return 1;
    const cards = Array.from(document.querySelectorAll('[data-platform-nav]'));
    if (!cards.length) return 4;
    const firstTop = cards[0].getBoundingClientRect().top;
    let columns = 0;
    for (const card of cards) {
      const top = card.getBoundingClientRect().top;
      if (Math.abs(top - firstTop) < 4) {
        columns += 1;
      } else {
        break;
      }
    }
    return Math.max(1, columns || 4);
  }, [useMobileLayout]);

  const selectNavByHorizontalDelta = useCallback((delta) => {
    if (!navItems.length) return;

    if (useMobileLayout) {
      let next = selectedNavIndex + delta;
      while (next < 0) next += navItems.length;
      next = next % navItems.length;
      controllerSelectionChangePendingRef.current = true;
      setSelectedNavIndex(next);
      return;
    }

    const platformCount = platformNavItems.length;
    const specialCount = specialNavItems.length;
    if (!platformCount) return;

    const specialStart = platformCount;
    const inSpecialRow = selectedNavIndex >= specialStart;

    if (inSpecialRow && specialCount > 0) {
      const currentSpecialIndex = selectedNavIndex - specialStart;
      const nextSpecialIndex = ((currentSpecialIndex + delta) % specialCount + specialCount) % specialCount;
      controllerSelectionChangePendingRef.current = true;
      setSelectedNavIndex(specialStart + nextSpecialIndex);
      return;
    }

    const nextPlatformIndex = ((selectedNavIndex + delta) % platformCount + platformCount) % platformCount;
    controllerSelectionChangePendingRef.current = true;
    setSelectedNavIndex(nextPlatformIndex);
  }, [useMobileLayout, navItems.length, platformNavItems.length, selectedNavIndex, specialNavItems.length]);

  const selectNavByVerticalDelta = useCallback((delta) => {
    if (!navItems.length) return;

    if (useMobileLayout) {
      const step = getPlatformGridStep();
      let next = selectedNavIndex + (delta > 0 ? step : -step);
      while (next < 0) next += navItems.length;
      next = next % navItems.length;
      controllerSelectionChangePendingRef.current = true;
      setSelectedNavIndex(next);
      return;
    }

    const platformCount = platformNavItems.length;
    const specialCount = specialNavItems.length;
    if (!platformCount) return;

    const specialStart = platformCount;
    const inSpecialRow = selectedNavIndex >= specialStart;

    if (delta > 0 && !inSpecialRow && specialCount > 0) {
      controllerSelectionChangePendingRef.current = true;
      setSelectedNavIndex(specialStart);
      return;
    }

    if (delta < 0 && inSpecialRow) {
      const fallbackPlatformIndex = Math.min(
        Math.max(lastDesktopPlatformIndexRef.current, 0),
        platformCount - 1
      );
      controllerSelectionChangePendingRef.current = true;
      setSelectedNavIndex(fallbackPlatformIndex);
    }
  }, [getPlatformGridStep, useMobileLayout, navItems.length, platformNavItems.length, selectedNavIndex, specialNavItems.length]);

  useEffect(() => {
    if (!useMobileLayout && selectedNavIndex < platformNavItems.length) {
      lastDesktopPlatformIndexRef.current = selectedNavIndex;
      const selectedPath = navItems[selectedNavIndex]?.path;
      if (selectedPath) {
        sessionStorage.setItem(PLATFORM_SELECTION_STORAGE_KEY, selectedPath);
      }
    }
  }, [useMobileLayout, navItems, platformNavItems.length, selectedNavIndex]);

  useEffect(() => {
    if (useMobileLayout || desktopSelectionInitializedRef.current || !platformNavItems.length) return;
    const savedPath = sessionStorage.getItem(PLATFORM_SELECTION_STORAGE_KEY);
    if (!savedPath) {
      desktopSelectionInitializedRef.current = true;
      return;
    }
    const savedIndex = platformNavItems.findIndex((item) => item.path === savedPath);
    if (savedIndex >= 0) {
      setSelectedNavIndex(savedIndex);
      lastDesktopPlatformIndexRef.current = savedIndex;
    }
    desktopSelectionInitializedRef.current = true;
  }, [useMobileLayout, platformNavItems]);

  useEffect(() => {
    if (useMobileLayout) {
      desktopSelectionInitializedRef.current = false;
    }
  }, [useMobileLayout]);

  const navigateToPath = useCallback((path) => {
    if (!path) return;
    if (path.startsWith('/platforms/')) {
      sessionStorage.setItem(PLATFORM_TO_GAMES_TRANSITION_KEY, String(Date.now()));
      sessionStorage.setItem(PLATFORM_SELECTION_STORAGE_KEY, path);
    }
    navigate(path);
  }, [navigate]);

  const handleNavSelect = useCallback((index) => {
    controllerSelectionChangePendingRef.current = true;
    setSelectedNavIndex(index);
    const path = navItems[index]?.path;
    if (path) {
      navigateToPath(path);
    }
  }, [navItems, navigateToPath]);

  useEffect(() => {
    if (useMobileLayout) return;

    const handleKeyboardNavigation = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.closest('input, textarea, select, [role="textbox"]'))
      ) {
        return;
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        selectNavByHorizontalDelta(event.key === 'ArrowLeft' ? -1 : 1);
      } else if (event.key === 'Enter' && !event.repeat) {
        const isPlatformNavigationTarget =
          target instanceof HTMLElement && target.closest('[data-platform-nav]');
        if (!isPlatformNavigationTarget && target instanceof HTMLElement && target.closest('button, a')) {
          return;
        }

        const selectedItem = navItems[selectedNavIndex];
        if (!selectedItem) return;
        event.preventDefault();
        navigateToPath(selectedItem.path);
      }
    };

    window.addEventListener('keydown', handleKeyboardNavigation);
    return () => window.removeEventListener('keydown', handleKeyboardNavigation);
  }, [useMobileLayout, selectNavByHorizontalDelta, navItems, selectedNavIndex, navigateToPath]);

  useEffect(() => { isMobileRef.current = useMobileLayout; }, [useMobileLayout]);
  useEffect(() => { controllerNeedsInteractionRef.current = controllerNeedsInteraction; }, [controllerNeedsInteraction]);
  useEffect(() => { selectedNavIndexRef.current = selectedNavIndex; }, [selectedNavIndex]);
  useEffect(() => { navItemsRef.current = navItems; }, [navItems]);
  selectNavByHorizontalDeltaRef.current = selectNavByHorizontalDelta;
  selectNavByVerticalDeltaRef.current = selectNavByVerticalDelta;
  navigateToPathRef.current = navigateToPath;

  useEffect(() => {
    if (
      controllerSelectionChangePendingRef.current &&
      selectedNavIndex !== previousSelectedNavIndexRef.current
    ) {
      playControllerSound('select');
    }
    previousSelectedNavIndexRef.current = selectedNavIndex;
    controllerSelectionChangePendingRef.current = false;
  }, [playControllerSound, selectedNavIndex]);

  function getConsoleLogoPath(platformName) {
    try {
      return require(`./assets/console-logos/${platformName.replace(/ /g, '_')}.png`);
    } catch (e) {
      return require(`./assets/console-logos/default.png`);
    }
  }

  function getHomePageLogoPath(platformName) {
    const normalized = platformName.replace(/ /g, '_');
    try {
      return require(`./assets/home-page-logos/${normalized}.png`);
    } catch (e) {
      return getConsoleLogoPath(platformName);
    }
  }

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const loadPlatforms = async (attempt = 0) => {
      try {
        if (!window.__platformsCache?.length) {
          setLoadingPlatforms(true);
        }
        setPlatformsLoadError('');
        const res = await axios.get('https://gamecollection-app.onrender.com/api/platforms');
        if (cancelled) return;
        const nextPlatforms = Array.isArray(res.data) ? res.data : [];
        setPlatforms(nextPlatforms);
        window.__platformsCache = nextPlatforms;
        setLoadingPlatforms(false);
      } catch (error) {
        if (cancelled) return;
        if (attempt < 1) {
          retryTimer = setTimeout(() => {
            loadPlatforms(attempt + 1);
          }, 500);
          return;
        }
        console.error('Failed to load platforms', error);
        setLoadingPlatforms(false);
        setPlatformsLoadError('Failed to load platforms.');
      }
    };

    loadPlatforms();

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [platformsReloadToken]);

  useEffect(() => {
    if (!navItems.length) return;
    if (selectedNavIndex >= navItems.length) {
      setSelectedNavIndex(0);
    }
  }, [navItems, selectedNavIndex]);

  useEffect(() => {
    const getPrimaryGamepad = () => {
      if (!navigator.getGamepads) return null;
      const pads = navigator.getGamepads();
      for (let i = 0; i < pads.length; i += 1) {
        if (pads[i] && pads[i].connected) {
          return pads[i];
        }
      }
      return null;
    };

    const updateGamepadConnection = (pad) => {
      const nextConnected = Boolean(pad);
      const nextName = pad?.id || '';
      if (gamepadConnectionStateRef.current.connected !== nextConnected) {
        gamepadConnectionStateRef.current.connected = nextConnected;
        setGamepadConnected(nextConnected);
      }
      if (gamepadConnectionStateRef.current.name !== nextName) {
        gamepadConnectionStateRef.current.name = nextName;
        setGamepadName(nextName);
      }
    };

    const runControllerLoop = () => {
      const pad = getPrimaryGamepad();
      updateGamepadConnection(pad);

      if (pad && !isMobileRef.current) {
        const now = performance.now();
        const isPressed = (buttonIndex) => Boolean(pad.buttons?.[buttonIndex]?.pressed);
        if (window.__controllerBackHeld === undefined) {
          window.__controllerBackHeld = false;
        }
        const backPressed = isPressed(1);
        if (!backPressed) {
          window.__controllerBackHeld = false;
        }
        const backJustPressed = backPressed && !window.__controllerBackHeld;
        if (backJustPressed) {
          window.__controllerBackHeld = true;
        }
        const hasAnyInput = () =>
          Boolean(pad.buttons?.some((button) => button?.pressed)) ||
          Math.abs(pad.axes?.[0] || 0) > 0.35 ||
          Math.abs(pad.axes?.[1] || 0) > 0.35;
        const shouldHandleButton = (buttonIndex, repeat = false) => {
          const pressed = isPressed(buttonIndex);
          const existingState = gamepadRepeatStateRef.current[buttonIndex];
          if (!pressed) {
            delete gamepadRepeatStateRef.current[buttonIndex];
            return false;
          }
          if (!existingState) {
            gamepadRepeatStateRef.current[buttonIndex] = {
              heldSince: now,
              lastTriggeredAt: now,
            };
            return true;
          }
          if (!repeat) return false;
          const heldFor = now - existingState.heldSince;
          const sinceLastTrigger = now - existingState.lastTriggeredAt;
          if (
            heldFor >= CONTROLLER_REPEAT_INITIAL_DELAY_MS &&
            sinceLastTrigger >= CONTROLLER_REPEAT_INTERVAL_MS
          ) {
            existingState.lastTriggeredAt = now;
            return true;
          }
          return false;
        };

        if (controllerNeedsInteractionRef.current && hasAnyInput()) {
          controllerNeedsInteractionRef.current = false;
          setControllerNeedsInteraction(false);
          gamepadRepeatStateRef.current = {};
          gamepadRafRef.current = requestAnimationFrame(runControllerLoop);
          return;
        }

        if (!controllerNeedsInteractionRef.current) {
          if (shouldHandleButton(0) && navItemsRef.current[selectedNavIndexRef.current]) {
            playControllerSound('confirm');
            navigateToPathRef.current?.(navItemsRef.current[selectedNavIndexRef.current].path);
          }
          if (backJustPressed) {
            playControllerSound('back');
            navigate(-1);
          }
          if (shouldHandleButton(14, true)) {
            selectNavByHorizontalDeltaRef.current?.(-1);
          }
          if (shouldHandleButton(15, true)) {
            selectNavByHorizontalDeltaRef.current?.(1);
          }
          if (shouldHandleButton(12, true)) {
            selectNavByVerticalDeltaRef.current?.(-1);
          }
          if (shouldHandleButton(13, true)) {
            selectNavByVerticalDeltaRef.current?.(1);
          }
        }
      } else {
        gamepadRepeatStateRef.current = {};
      }

      gamepadRafRef.current = requestAnimationFrame(runControllerLoop);
    };

    const onGamepadConnect = () => updateGamepadConnection(getPrimaryGamepad());
    const onGamepadDisconnect = () => updateGamepadConnection(getPrimaryGamepad());

    window.addEventListener('gamepadconnected', onGamepadConnect);
    window.addEventListener('gamepaddisconnected', onGamepadDisconnect);
    gamepadRafRef.current = requestAnimationFrame(runControllerLoop);

    return () => {
      window.removeEventListener('gamepadconnected', onGamepadConnect);
      window.removeEventListener('gamepaddisconnected', onGamepadDisconnect);
      if (gamepadRafRef.current) {
        cancelAnimationFrame(gamepadRafRef.current);
      }
    };
  }, [navigate, playControllerSound]);

  useEffect(() => {
    if (previousGamepadConnectedRef.current === null) {
      previousGamepadConnectedRef.current = gamepadConnected;
      if (gamepadConnected) {
        sessionStorage.setItem(CONTROLLER_CONNECTED_STATE_KEY, '1');
      } else {
        sessionStorage.removeItem(CONTROLLER_CONNECTED_STATE_KEY);
      }
      return;
    }
    if (previousGamepadConnectedRef.current === gamepadConnected) {
      return;
    }
    if (gamepadConnected) {
      showControllerToast(`🎮 ${gamepadName || 'Controller'} connected`);
      sessionStorage.setItem(CONTROLLER_CONNECTED_STATE_KEY, '1');
    } else {
      showControllerToast('🎮 Controller disconnected');
      sessionStorage.removeItem(CONTROLLER_CONNECTED_STATE_KEY);
    }
    previousGamepadConnectedRef.current = gamepadConnected;
  }, [gamepadConnected, gamepadName, showControllerToast]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        window.focus();
        if (gamepadConnected && !useMobileLayout) {
          setControllerNeedsInteraction(true);
        }
      }
    };

    const handleWindowBlur = () => {
      if (gamepadConnected && !useMobileLayout) {
        setControllerNeedsInteraction(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [gamepadConnected, useMobileLayout]);

  useEffect(() => {
    if (!pendingSelectedPathRef.current) return;
    const nextIndex = navItems.findIndex((item) => item.path === pendingSelectedPathRef.current);
    if (nextIndex >= 0) {
      setSelectedNavIndex(nextIndex);
    }
    pendingSelectedPathRef.current = null;
  }, [navItems]);

  useEffect(() => {
    if (!controllerNeedsInteraction) return;
    const rearmController = () => {
      setControllerNeedsInteraction(false);
      window.focus();
    };
    window.addEventListener('pointerdown', rearmController);
    window.addEventListener('keydown', rearmController);
    return () => {
      window.removeEventListener('pointerdown', rearmController);
      window.removeEventListener('keydown', rearmController);
    };
  }, [controllerNeedsInteraction]);

  useEffect(() => {
    axios.get('https://gamecollection-app.onrender.com/api/recent')
      .then(res => setNewGames(res.data))
      .catch(console.error);
  }, []);

  if (loadingPlatforms && !platforms.length) {
    return (
      <>
        {showEntryWhiteOverlay && (
          <Box
            className={`whiteFade${entryWhiteOverlayFadeOut ? ' fadeOut' : ''}`}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 4000,
              pointerEvents: 'none',
            }}
          />
        )}
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (platformsLoadError && !platforms.length) {
    return (
      <>
        {showEntryWhiteOverlay && (
          <Box
            className={`whiteFade${entryWhiteOverlayFadeOut ? ' fadeOut' : ''}`}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 4000,
              pointerEvents: 'none',
            }}
          />
        )}
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mt={8} gap={2}>
          <Box sx={{ fontWeight: 700, color: '#333' }}>{platformsLoadError}</Box>
          <button
            type="button"
            className="snazzy-button"
            onClick={() => setPlatformsReloadToken((prev) => prev + 1)}
          >
            Retry
          </button>
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ }}>
      {showEntryWhiteOverlay && (
        <Box
          className={`whiteFade${entryWhiteOverlayFadeOut ? ' fadeOut' : ''}`}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 4000,
            pointerEvents: 'none',
          }}
        />
      )}
      {!isMobile && (
        <button
          type="button"
          className="platform-layout-toggle"
          aria-label="Toggle mobile layout"
          onClick={() => {
            pendingSelectedPathRef.current = navItems[selectedNavIndex]?.path || null;
            setForceMobileLayout((prev) => !prev);
          }}
          title={forceMobileLayout ? 'Switch to desktop layout' : 'Switch to mobile layout'}
        >
          =
        </button>
      )}
      {useMobileLayout && (
        <div className="center-box">
          <img className='circle rock' src='https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2Fstatic.vecteezy.com%2Fsystem%2Fresources%2Fpreviews%2F017%2F178%2F221%2Foriginal%2Fnew-sticker-label-on-transparent-background-free-png.png&f=1&nofb=1&ipt=a40e4ced18d9e7129e1036261b80dd27804c3cb8777146bbd53d7569a6ea751e' alt="New"></img>
             {newGames.map(p => (
              <img key={p.title + p.url} className="circle" src={'https://gamecollection-app.onrender.com'+ p.url} alt={p.title || 'Recent'}></img>
             ))}
        </div>
      )}
      {useMobileLayout ? (
        <Box sx={{ px: 1.5, pb: 4 }}>
          {/* Special nav: horizontal scroll pill strip */}
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 1,
              pb: 1.5,
              mb: 1.5,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {specialNavItems.map((item, specialIndex) => {
              const isSelected = specialIndex === selectedNavIndex;
              return (
                <button
                  key={item.key}
                  type="button"
                  data-platform-nav={specialIndex}
                  onClick={() => handleNavSelect(specialIndex)}
                  style={{
                    flex: '0 0 auto',
                    border: isSelected ? '2px solid #4a90e2' : '1.5px solid rgba(255,255,255,0.55)',
                    borderRadius: 999,
                    background: isSelected ? 'rgba(74,144,226,0.92)' : 'rgba(255,255,255,0.72)',
                    color: isSelected ? '#fff' : '#1b2a3f',
                    padding: '7px 16px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    boxShadow: isSelected
                      ? '0 4px 12px rgba(74,144,226,0.38)'
                      : '0 2px 6px rgba(0,0,0,0.1)',
                  }}
                >
                  {item.alt}
                </button>
              );
            })}
          </Box>

          {/* Platform 2-column frosted-glass grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5,
            }}
          >
            {platformNavItems.map((item, platformIndex) => {
              const index = specialNavItems.length + platformIndex;
              const isSelected = index === selectedNavIndex;
              return (
                <Box
                  key={item.key}
                  component="button"
                  type="button"
                  data-platform-nav={index}
                  onClick={() => handleNavSelect(index)}
                  sx={{
                    background: isSelected
                      ? 'rgba(74,144,226,0.18)'
                      : 'rgba(255,255,255,0.64)',
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    border: isSelected
                      ? '2px solid #4a90e2'
                      : '1.5px solid rgba(255,255,255,0.52)',
                    borderRadius: 3,
                    boxShadow: isSelected
                      ? '0 6px 20px rgba(74,144,226,0.28)'
                      : '0 3px 10px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1.5,
                    gap: 0.75,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:active': { transform: 'scale(0.97)' },
                  }}
                >
                  <Box
                    component="img"
                    src={getHomePageLogoPath(item.logo)}
                    alt={item.alt}
                    sx={{
                      width: '100%',
                      maxWidth: 110,
                      height: 60,
                      objectFit: 'contain',
                      filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.45))',
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/home-page-logos/default.png';
                    }}
                  />
                  <Box
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      color: isSelected ? '#1a3a6e' : '#1e2a3a',
                      textAlign: 'center',
                      lineHeight: 1.3,
                    }}
                  >
                    {item.alt}
                  </Box>
                  {item.gameCount > 0 && (
                    <Box
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: isSelected ? '#4a90e2' : '#5a7a9a',
                        background: isSelected
                          ? 'rgba(74,144,226,0.12)'
                          : 'rgba(0,0,0,0.06)',
                        px: 1,
                        borderRadius: 999,
                      }}
                    >
                      {item.gameCount} games
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box className="platform-coverflow-shell">
          <Box className="platform-coverflow-stage" ref={coverflowStageRef}>
            {platformNavItems.map((item, index) => {
              const activePlatformIndex =
                selectedNavIndex < platformNavItems.length
                  ? selectedNavIndex
                  : lastDesktopPlatformIndexRef.current;
              const offset = index - activePlatformIndex;
              const absoluteOffset = Math.abs(offset);
              const visible = absoluteOffset <= 2;
              const translateX = offset * 560;
              const rotateY = Math.max(-50, Math.min(50, -offset * 18));
              const translateZ = Math.max(-320, 130 - absoluteOffset * 54);
              const scale = Math.max(0.46, 1 - absoluteOffset * 0.12);
              const isSelected = index === selectedNavIndex;

              return (
                <div
                  key={item.key}
                  className={`platform-coverflow-card${isSelected ? ' platform-coverflow-card-selected' : ''}`}
                  data-platform-nav={index}
                  style={{
                    zIndex: 300 - absoluteOffset,
                    transform: `translate3d(${translateX}px, 0, ${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
                    opacity: 1,
                    pointerEvents: visible ? 'auto' : 'none',
                  }}
                >
                  <button
                    type="button"
                    className="platform-coverflow-hit-area"
                    onClick={() => {
                      handleNavSelect(index);
                    }}
                  >
                    <img
                      src={getHomePageLogoPath(item.logo)}
                      alt={item.alt}
                      className={`platform-coverflow-logo ${isSelected ? 'platform-coverflow-logo-selected' : 'platform-coverflow-logo-dimmed'}`}
                      style={{
                        filter: 'drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.55))',
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/home-page-logos/default.png';
                      }}
                    />
                  </button>
                </div>
              );
            })}
          </Box>
          <Box className="platform-desktop-specials">
            {specialNavItems.map((item, specialIndex) => {
              const navIndex = platformNavItems.length + specialIndex;
              const isSpecialSelected = selectedNavIndex === navIndex;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`platform-special-bubble${isSpecialSelected ? ' platform-special-bubble-selected' : ''}`}
                  data-platform-nav={navIndex}
                  onClick={() => handleNavSelect(navIndex)}
                >
                  {item.alt}
                </button>
              );
            })}
          </Box>
          <Box className="platform-coverflow-caption">
            {navItems[selectedNavIndex]?.label || navItems[selectedNavIndex]?.alt || ''}
          </Box>
        </Box>
      )}
      {controllerToast.open && (
        <Box
          sx={{
            position: 'fixed',
            left: 16,
            bottom: 16,
            zIndex: 2000,
            bgcolor: 'rgba(20,20,20,0.9)',
            color: '#fff',
            px: 2,
            py: 1.2,
            borderRadius: 2,
            boxShadow: 6,
            fontWeight: 700,
            pointerEvents: 'none',
            animation: 'controller-toast-rise-out 2200ms ease forwards',
          }}
        >
          {controllerToast.message}
        </Box>
      )}
    </Box>
  );
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}